/* import __COLOCATED_TEMPLATE__ from './conversation-history-for-copilot.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { PulseAccordion } from 'glint/pulse';
import { inject as service } from '@ember/service';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type CopilotSettings from 'embercom/models/copilot-settings';
import type CopilotApiService from 'embercom/services/copilot-api';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type Store from '@ember-data/store';
import type ExternalAgent from 'embercom/models/standalone/external-agent';
import { EntityType } from 'embercom/models/data/entity-types';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';

interface Args {
  accordion: PulseAccordion;
  closeAccordion?: () => void;
  copilotSettings: CopilotSettings | null;
  isSyncEnabled: boolean;
}

interface Signature {
  Args: Args;
}

export default class ConversationHistoryForCopilot extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare copilotApi: CopilotApiService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare store: Store;

  @tracked externalAgents: ExternalAgent[] = [];

  ALL_ADMINS_ID = -1;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.fetchExternalAgents).perform();
  }

  @dropTask
  *fetchExternalAgents() {
    let agents: ExternalAgent[] = yield this.store.findAll('standalone/external-agent');
    this.externalAgents = agents.toArray().sortBy('name');
  }

  get allAdmins() {
    return this.externalAgents.map((externalAgent: ExternalAgent) => ({
      id: Number(externalAgent.id),
      name: externalAgent.name,
    }));
  }

  get hasSufficientConversationData() {
    return this.importedConversationCount > 10;
  }

  get importedConversationCount() {
    return this.knowledgeHubService.usageSummary?.[EntityType.ConversationExcerpt]?.all || 0;
  }

  get useExcerpts() {
    return this.args.copilotSettings?.usePastConversations ?? false;
  }

  get selectedAdmins() {
    return this.args.copilotSettings?.trustedAdminIds ?? [];
  }

  get selectedAdminLabel() {
    if (!this.useExcerpts) {
      return this.intl.t(
        'standalone.common.conversation-ingestion.conversation-history-for-copilot.none-teammates-label',
      );
    }
    if (this.allAdminsEnabled || this.selectedAdmins.length === 0) {
      return this.allTeammatesLabel;
    } else {
      return this.intl.t(
        'standalone.common.conversation-ingestion.conversation-history-for-copilot.count-teammates-label',
        {
          count: this.selectedAdmins.length,
        },
      );
    }
  }

  get allTeammatesLabel() {
    return this.intl.t(
      'standalone.common.conversation-ingestion.conversation-history-for-copilot.all-teammates-label',
    );
  }

  get allAdminsEnabled() {
    return this.allAdmins.every((admin) => this.selectedAdmins.includes(admin.id));
  }

  get listedAdmins(): { text: string; description?: string; value: number; isSelected: boolean }[] {
    return [
      {
        text: this.allTeammatesLabel,
        value: this.ALL_ADMINS_ID,
        isSelected: this.selectedAdmins.length === 0 || this.allAdminsEnabled,
      },
      ...this.allAdmins.map((admin) => ({
        text: admin.name,
        value: admin.id,
        isSelected: this.selectedAdmins.includes(admin.id),
      })),
    ];
  }

  get hasInstalledCopilot(): boolean {
    return this.appService.mainStandalonePlatform.hasInstalledCopilot;
  }

  get shouldAllowChangesCopilotToggle() {
    return (
      this.args.isSyncEnabled &&
      this.hasInstalledCopilot &&
      !this.copilotApi.isCopilotSettingsLoading
    );
  }

  get hasExcerptsEnabled() {
    return this.args.copilotSettings?.usePastConversations;
  }

  @action
  updateExcerpts(): void {
    if (!this.args.copilotSettings) {
      return;
    }
    this.args.copilotSettings.usePastConversations =
      !this.args.copilotSettings.usePastConversations;
  }

  @action
  onSelectedTeammate(id: number): void {
    if (!this.args.copilotSettings) {
      return;
    }

    if (id === this.ALL_ADMINS_ID) {
      this.toggleAllAdmins();
      return;
    }

    let currentAdmins = this.args.copilotSettings.trustedAdminIds;
    if (currentAdmins.includes(id)) {
      this.args.copilotSettings.trustedAdminIds = currentAdmins.filter((adminId) => adminId !== id);
    } else {
      this.args.copilotSettings.trustedAdminIds = [...currentAdmins, id];
    }
  }

  private toggleAllAdmins(): void {
    if (!this.args.copilotSettings) {
      return;
    }

    if (this.allAdminsEnabled) {
      this.args.copilotSettings.trustedAdminIds = [];
    } else {
      this.args.copilotSettings.trustedAdminIds = this.allAdmins.map((admin) => admin.id);
    }
  }

  @action
  deployCopilot(): void {
    let route =
      this.appService.mainStandalonePlatform.componentData['conversation-ingestion']
        ?.deployCopilotRoute;
    if (route) {
      this.router.transitionTo(route);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ConversationIngestion::ConversationHistoryForCopilot': typeof ConversationHistoryForCopilot;
  }
}
